/**
 * What a client tells the server it can render of Marvin's output (`X-Marvin-Renders`).
 *
 * ai_engine names every unit a client renders (`agent/output_vocabulary.py`: `part.*`,
 * `activity.*`, `nav.*`, `widget.*`, `event.*`, `decide.action`, `answer.question`,
 * `link.viewer`) and rewrites each unit a request does not declare into that unit's fallback
 * (dropped, or text plus a link), on live and reconnected streams and on loaded and shared
 * conversations. A request without the header gets the baseline frozen from the 2.71.21 iOS
 * build. The header names units exactly; the server ignores a name it does not know.
 *
 * Each host declares its set on `Platform.marvinRenders`: the web and the desktop run the web
 * Marvin and render the whole vocabulary; the iOS shell renders natively and declares its set in
 * `native/bridgeManifest.json` (`marvinRenders`), which its Swift tests read too. Every request
 * sends it, with the host's other declarations (`api/clientDeclaration.ts`).
 */
import vocabulary from "./marvinOutput.generated.json";

/** What a client that does not declare a unit gets: nothing, text in its place, or (for a
 *  capability) the same output without the interaction. `required` is always sent. */
export type MarvinUnitFallback = "required" | "dropped" | "text" | "capability";

/** The vocabulary as ai_engine's generator writes it (`agent.output_vocabulary.vocabulary_json`). */
interface MarvinOutputVocabulary {
    digest: string;
    header: string;
    required: string[];
    units: ReadonlyArray<{ unit: string; fallback: MarvinUnitFallback; note?: string }>;
    /** The server's named client sets: `baseline` (no header), `ios-native`, `web`. */
    sets: Record<string, string[]>;
}

/** Generated by ai_engine (`scripts/generate_navigation_contract_types.py`); regenerate, never edit. */
const VOCABULARY = vocabulary as MarvinOutputVocabulary;

export const MARVIN_RENDERS_HEADER = VOCABULARY.header;

/** Every unit the vocabulary names. */
export const MARVIN_OUTPUT_UNITS: readonly string[] = VOCABULARY.units.map(({ unit }) => unit);

/** Each unit's fallback, by unit. */
export const MARVIN_UNIT_FALLBACKS: ReadonlyMap<string, MarvinUnitFallback> = new Map(
    VOCABULARY.units.map(({ unit, fallback }) => [unit, fallback]),
);

/** The server's named client sets (`baseline`, `ios-native`, `web`), as the vocabulary records them. */
export const MARVIN_CLIENT_SETS: Readonly<Record<string, readonly string[]>> = VOCABULARY.sets;

/** The web Marvin renders every unit (the web and the desktop host). */
export const WEB_MARVIN_RENDERS: readonly string[] = MARVIN_OUTPUT_UNITS;

/** The entries of a declared set the vocabulary does not name (the server would ignore them). */
export function unknownUnits(declared: readonly string[]): string[] {
    const known = new Set(MARVIN_OUTPUT_UNITS);
    return declared.filter((unit) => !known.has(unit));
}
